import alertService from '../services/alert.service'
import api, {
    PaginateRequest,
    PaginateResponse,
    ResponseCallback,
    ResponseAndErrorCallback
} from '../services/api.service'
import routeUtil from '../utils/route.util'
import {Markup} from '../views/LabelBuilder/LabelBuilder.slice'
import {CustomLabelDataData} from './customLabelData.api'
import {LabelApprovalData} from './labelApproval.api'
import {CustomLabelPrintData} from './customLabelPrint.api'
import {ItemData} from './item.api'
import {UserData} from './user.api'

export interface CustomLabelData {
    id: number
    siteId: number
    createdBy: number
    updatedBy: number
    deletedBy?: number
    name: string
    csvFilename?: string
    apiUrl?: string
    printNodePrinterId?: number | string | null
    /** Opt-in design history, set on the create step. */
    keepVersionHistory?: boolean
    deletedAt?: string
    createdAt: string
    updatedAt: string
    remainingPrintablesCount: number
    planRemainingPrintablesCount: number
    printedCount: number
    hasPrintableLimit: boolean
    attributes: Record<string, any>
    updatedUser?: UserData
    item?: ItemData
    datables: CustomLabelDataData['datable'][]
    printed: Record<
        Exclude<CustomLabelDataData['datable'], 'item'>,
        { total: number, printed: number }
    >
    /** Latest approval request for this label, null when never sent. */
    approval?: LabelApprovalData | null
    /** Preview image of the saved design; empty string when none exists yet. */
    snapshot?: string
}

export interface CustomLabelCreateRequest {
    name?: string
    item?: number
    csv?: File | null
    csvFilename?: string
    apiUrl?: string
    printNodePrinterId?: number | string
    keepVersionHistory?: boolean | string
}

type CustomLabelPaginateResponse = PaginateResponse<CustomLabelData[]>
type CustomLabelPaginatePrintsResponse = PaginateResponse<CustomLabelPrintData[]>

export interface CustomLabelPaginatePrintsRequest extends PaginateRequest {
    startAt?: string
    endAt?: string
}

export interface CustomLabelUpdateRequest {
    name?: string
    item?: number
    csv?: File | null
    csvFilename?: string
    apiUrl?: string
    printNodePrinterId?: number | string
    keepVersionHistory?: boolean | string
}

export interface CustomLabelPrintRequest {
    amount?: number
}

export interface CustomLabelReprintPrintsRequest {
    prints?: number[]
    /** Reproduce each job with the design it was printed from, not the latest. */
    useOriginalVersion?: boolean
}

export interface CustomLabelDeletePrintsRequest {
    prints?: number[]
}

interface CustomLabelDeletePrintsResponse {
    deleted: boolean
}

export type ApiConditionOperator =
    | 'equals'
    | 'not_equals'
    | 'contains'
    | 'not_contains'
    | 'starts_with'
    | 'ends_with'
    | 'greater_than'
    | 'greater_than_or_equal'
    | 'less_than'
    | 'less_than_or_equal'
    | 'is_empty'
    | 'is_not_empty'
    | 'is_true'
    | 'is_false'

export interface ApiFieldCondition {
    field: string
    operator: ApiConditionOperator
    value?: string | number | boolean | null
}

export interface ApiFieldConditionalRule {
    combinator: 'and' | 'or'
    conditions: ApiFieldCondition[]
    result: {type: 'literal', value: string | number | boolean | null}
}

export interface ApiCalculationOperand {
    type: 'field' | 'number'
    value: string | number
}

export interface ApiCalculatedField {
    target_field: string
    type: 'conditional' | 'calculation'
    rules?: ApiFieldConditionalRule[]
    calculation?: {
        operator: 'add' | 'subtract' | 'multiply' | 'divide'
        operands: [ApiCalculationOperand, ApiCalculationOperand]
    }
    fallback: {type: 'original' | 'literal', value?: string | number | boolean | null}
}

export interface CustomFieldResponse {
    attributes: {
        id: number
        customLabelId: number
        datable: string
        api_field: string[] | string
        sample_data?: string[] | string
        pii_fields?: string[] | string
        conditional_rules?: ApiCalculatedField[] | string
        resolved_sample_data?: Array<string | number | boolean | null> | string
        siteId: number
        created_at: string
        updated_at: string
    }
}

const routes = {
    create: '/customLabels',
    read: '/customLabels/{id}',
    paginate: '/customLabels/paginate',
    search: '/customLabels/search',
    paginatePrints: '/customLabels/{id}/prints/paginate',
    update: '/customLabels/{id}',
    preview: '/customLabels/{id}/preview',
    print: '/customLabels/{id}/print',
    delete: '/customLabels/{id}',
    reprintPrints: '/customLabels/{id}/prints/reprint',
    deletePrints: '/customLabels/{id}/prints/delete',
    customField: 'customApiField/create/{id}',
    getCustomField: 'customApiField/{id}'
}

export default {
    create(
        payload: CustomLabelCreateRequest,
        callback: ResponseCallback<CustomLabelData>
    ) {
        api.postForm(routes.create, payload, callback)
    },

    createWithError(
        payload: CustomLabelCreateRequest,
        callback: ResponseCallback<CustomLabelData>,
        errorCallback: ResponseAndErrorCallback
    ) {
        api.postFormAndError(routes.create, payload, callback, errorCallback)
    },

    read(id: number, callback: ResponseCallback<CustomLabelData>) {
        const url = routeUtil.replaceParams(routes.read, {id})
        api.get(url, {}, callback)
    },

    paginate(
        params: PaginateRequest,
        callback: ResponseCallback<CustomLabelPaginateResponse>
    ) {
        api.get(routes.paginate, params, callback)
    },

    search(
        term: string,
        callback: ResponseCallback<CustomLabelData[]>
    ) {
        const params = {term}
        api.get(routes.search, params, callback)
    },

    paginatePrints(
        id: number,
        params: CustomLabelPaginatePrintsRequest,
        callback: ResponseCallback<CustomLabelPaginatePrintsResponse>) {
        const url = routeUtil.replaceParams(routes.paginatePrints, {id})
        api.get(url, params, callback)
    },

    update(
        id: number,
        payload: CustomLabelUpdateRequest,
        callback: ResponseCallback<CustomLabelData>
    ) {
        const url = routeUtil.replaceParams(routes.update, {id})
        api.postForm(url, payload, callback)
    },

    preview(id: number, callback: ResponseCallback<Markup>) {
        const url = routeUtil.replaceParams(routes.preview, {id})
        // alertService.info('Custom label preview generating...')
        api.get(url, {}, callback)
    },

    print(
        id: number,
        payload: CustomLabelPrintRequest,
        callback: ResponseCallback<Markup[]>
    ) {
        const url = routeUtil.replaceParams(routes.print, {id})
        alertService.info('Custom labels generating...')
        api.post(url, payload, callback)
    },

    reprintPrints(
        id: number,
        payload: CustomLabelReprintPrintsRequest,
        callback: ResponseCallback<Markup[]>
    ) {
        const url = routeUtil.replaceParams(routes.reprintPrints, {id})
        alertService.info('Custom labels generating...')
        api.post(url, payload, callback)
    },

    delete(id: number, callback: ResponseCallback<CustomLabelData>) {
        const url = routeUtil.replaceParams(routes.delete, {id})
        api.delete(url, {}, callback)
    },

    deletePrints(
        id: number,
        payload: CustomLabelDeletePrintsRequest,
        callback: ResponseCallback<CustomLabelDeletePrintsResponse>
    ) {
        const url = routeUtil.replaceParams(routes.deletePrints, {id})
        api.post(url, payload, callback)
    },

    customField(
        id: number,
        payload: Record<string, string | number | boolean>,
        callback: ResponseCallback<CustomLabelData>
    ) {
        const url = routeUtil.replaceParams(routes.customField, {id})
        api.postForm(url, payload, callback)
    },
    getCustomField(
        id: number,
        callback: ResponseCallback<CustomFieldResponse>  // Change this type
    ) {
        const url = routeUtil.replaceParams(routes.getCustomField, {id})
        api.get(url, {}, callback)
    }


}
